import { Box } from "@mui/material";
import { ReactNode } from "react";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  MINI_COG_BORDER_COLOR,
  MINI_COG_CLOCK_DRAW_OPTIONS,
  MINI_COG_PANEL_BACKGROUND,
  MINI_COG_SCORE_PLACEHOLDER,
  MINI_COG_TEXT,
  MINI_COG_WORD_RECALL_OPTIONS,
} from "../initial-f2f-mini-cog-constant";

export interface MiniCogScoringProps {
  wordRecallScore: string;
  clockDrawScore: string;
  total: number | null;
  onWordRecallChange: (score: string) => void;
  onClockDrawChange: (score: string) => void;
}

interface ScoreRowProps {
  control: ReactNode;
  description: string;
}

const ScoreRow = ({ control, description }: ScoreRowProps) => (
  <Flex
    gap={2}
    alignItems="flex-start"
    flexWrap="wrap"
    sx={{ borderTop: `1px solid ${MINI_COG_BORDER_COLOR}`, p: 1.5 }}
  >
    <Flex gap={1} alignItems="center" flexWrap="wrap" minWidth={200}>
      {control}
    </Flex>
    <Text size={13} c="text.secondary" flex={1} minWidth={240}>
      {description}
    </Text>
  </Flex>
);

const MiniCogScoring = ({
  wordRecallScore,
  clockDrawScore,
  total,
  onWordRecallChange,
  onClockDrawChange,
}: MiniCogScoringProps) => (
  <Box sx={{ border: `1px solid ${MINI_COG_BORDER_COLOR}`, borderRadius: 1 }}>
    <Box sx={{ bgcolor: MINI_COG_PANEL_BACKGROUND, px: 1.5, py: 1 }}>
      <Text size={13} fontWeight="bold">
        {MINI_COG_TEXT.scoringTitle}
      </Text>
    </Box>
    <ScoreRow
      description={MINI_COG_TEXT.wordRecallDescription}
      control={
        <>
          <Text size={13}>{MINI_COG_TEXT.wordRecallLabel}</Text>
          <Box width={72}>
            <CustomSelect
              options={MINI_COG_WORD_RECALL_OPTIONS}
              placeholder={MINI_COG_SCORE_PLACEHOLDER}
              value={wordRecallScore || null}
              onChange={(value) => onWordRecallChange(value ?? "")}
            />
          </Box>
          <Text size={13}>{MINI_COG_TEXT.wordRecallRange}</Text>
        </>
      }
    />
    <ScoreRow
      description={MINI_COG_TEXT.clockDrawDescription}
      control={
        <>
          <Text size={13}>{MINI_COG_TEXT.clockDrawLabel}</Text>
          <Box width={72}>
            <CustomSelect
              options={MINI_COG_CLOCK_DRAW_OPTIONS}
              placeholder={MINI_COG_SCORE_PLACEHOLDER}
              value={clockDrawScore || null}
              onChange={(value) => onClockDrawChange(value ?? "")}
            />
          </Box>
          <Text size={13}>{MINI_COG_TEXT.clockDrawRange}</Text>
        </>
      }
    />
    <ScoreRow
      description={MINI_COG_TEXT.totalScoreDescription}
      control={
        <Text size={13}>
          {`${MINI_COG_TEXT.totalScoreLabel} `}
          <Box component="span" fontWeight="bold">
            {total ?? MINI_COG_TEXT.totalScoreEmpty}
          </Box>
          {` ${MINI_COG_TEXT.totalScoreRange}`}
        </Text>
      }
    />
  </Box>
);

export default MiniCogScoring;
